/*
 * Pecas comuns das telas de chamados: etiquetas de situacao e prioridade,
 * seletor de imagens e o envio dos anexos depois da mensagem.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  TICKET_PRIORITY_LABELS,
  TICKET_STATUS_LABELS,
  type TicketPriority,
  type TicketStatus,
} from '@hotspot/shared';
import { anexarAoChamado } from '../lib/upload';
import {
  nomeDaLocalidade,
  type EstadoDoPrazoDto,
  type TicketListItemDto,
  type TicketSlaDto,
} from '../lib/types';
import { Badge } from './ui';

export const MAX_ANEXOS = 3;
const LIMITE_BYTES = 5 * 1024 * 1024;
const TIPOS = ['image/jpeg', 'image/png', 'image/webp'];

export const CAMPO =
  'w-full rounded-xl bg-white px-3.5 py-2.5 text-sm text-slate-900 ring-1 ring-slate-200 shadow-xs ring-inset placeholder:text-slate-400 hover:ring-slate-300 focus:ring-2 focus:ring-brand-500 focus:outline-none disabled:bg-slate-50';

const TOM_DA_SITUACAO: Record<string, 'info' | 'warning' | 'success' | 'neutral' | 'danger'> = {
  aberto: 'info',
  em_andamento: 'warning',
  pendente: 'neutral',
  finalizado: 'success',
  cancelado: 'neutral',
};

export function SituacaoBadge({ status }: { status: string }) {
  return (
    <Badge tone={TOM_DA_SITUACAO[status] ?? 'neutral'} dot>
      {TICKET_STATUS_LABELS[status as TicketStatus] ?? status}
    </Badge>
  );
}

export function PrioridadeBadge({ priority }: { priority: string }) {
  // Normal e baixa nao merecem cor: destaque so para o que pede pressa.
  const tom = priority === 'urgente' ? 'danger' : priority === 'alta' ? 'warning' : 'neutral';
  return <Badge tone={tom}>{TICKET_PRIORITY_LABELS[priority as TicketPriority] ?? priority}</Badge>;
}

/** "45 min", "3h 20min", "2d 4h". */
export function duracao(segundos: number): string {
  const minutos = Math.max(0, Math.round(segundos / 60));
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return minutos % 60 ? `${horas}h ${minutos % 60}min` : `${horas}h`;
  const dias = Math.floor(horas / 24);
  return horas % 24 ? `${dias}d ${horas % 24}h` : `${dias}d`;
}

/**
 * Um prazo em uma etiqueta: "vence em 40 min", "atrasado ha 2h", "no prazo
 * (35 min)". Com o chamado pendente, o relogio esta parado e a etiqueta diz.
 */
export function PrazoBadge({
  prazo,
  pausado,
  rotulo,
}: {
  prazo: EstadoDoPrazoDto;
  pausado: boolean;
  rotulo: string;
}) {
  if (prazo.segundos !== null && prazo.cumprido !== null) {
    return (
      <Badge tone={prazo.cumprido ? 'success' : 'danger'}>
        {rotulo}: {prazo.cumprido ? 'no prazo' : 'fora do prazo'} ({duracao(prazo.segundos)})
      </Badge>
    );
  }
  if (!prazo.venceEm) return null;

  if (pausado) return <Badge tone="neutral">{rotulo}: pausado (aguardando)</Badge>;

  const restante = (new Date(prazo.venceEm).getTime() - Date.now()) / 1000;
  if (restante <= 0 || prazo.cumprido === false) {
    return (
      <Badge tone="danger" dot>
        {rotulo}: atrasado há {duracao(-restante)}
      </Badge>
    );
  }
  // Menos de um quarto do prazo sobrando: hora de olhar.
  const apertado = restante < prazo.alvoMin * 15;
  return (
    <Badge tone={apertado ? 'warning' : 'neutral'}>
      {rotulo}: vence em {duracao(restante)}
    </Badge>
  );
}

/** O prazo que importa agora: a resposta, enquanto nao veio; depois, a solucao. */
export function PrazoAtual({ sla }: { sla: TicketSlaDto }) {
  const aguardandoResposta = sla.resposta.segundos === null && sla.resposta.venceEm !== null;
  return aguardandoResposta ? (
    <PrazoBadge prazo={sla.resposta} pausado={sla.pausado} rotulo="Resposta" />
  ) : (
    <PrazoBadge prazo={sla.solucao} pausado={sla.pausado} rotulo="Solução" />
  );
}

/** "Jardim Primavera · CHR HOTSPOT", ou "CHR HOTSPOT (todas as praças)". */
export function textoDoLocal(local: TicketListItemDto['local']): string | null {
  if (!local) return null;
  const equipamento = local.mikrotikName ?? 'equipamento removido';
  return local.vlan
    ? `${nomeDaLocalidade(local.vlan)} · ${equipamento}`
    : `${equipamento} (todas as praças)`;
}

/** "04/11 08:32", ou com o ano quando nao e o ano corrente. */
export function quando(iso: string): string {
  const data = new Date(iso);
  const mesmoAno = data.getFullYear() === new Date().getFullYear();
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    ...(mesmoAno ? {} : { year: 'numeric' }),
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Seletor de ate tres imagens, com miniatura e checagem de tipo e tamanho
 * antes de enviar — a API checa de novo, mas recusar so depois do envio de
 * 5 MB e desperdicio de paciencia.
 */
export function SeletorDeImagens({
  arquivos,
  onChange,
  disabled,
}: {
  arquivos: File[];
  onChange: (arquivos: File[]) => void;
  disabled?: boolean;
}) {
  const [aviso, setAviso] = useState<string | null>(null);
  const miniaturas = useMemo(
    () => arquivos.map((arquivo) => URL.createObjectURL(arquivo)),
    [arquivos],
  );

  useEffect(
    () => () => {
      for (const url of miniaturas) URL.revokeObjectURL(url);
    },
    [miniaturas],
  );

  function escolher(lista: FileList | null) {
    if (!lista) return;
    const novos: File[] = [];
    const recusados: string[] = [];

    for (const arquivo of Array.from(lista)) {
      if (!TIPOS.includes(arquivo.type)) recusados.push(`${arquivo.name} não é imagem`);
      else if (arquivo.size > LIMITE_BYTES) recusados.push(`${arquivo.name} passa de 5 MB`);
      else novos.push(arquivo);
    }

    const juntos = [...arquivos, ...novos];
    if (juntos.length > MAX_ANEXOS) recusados.push(`no máximo ${MAX_ANEXOS} imagens`);
    setAviso(recusados.length > 0 ? `Não anexado: ${recusados.join('; ')}.` : null);
    onChange(juntos.slice(0, MAX_ANEXOS));
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {arquivos.map((arquivo, indice) => (
          <div key={`${arquivo.name}-${indice}`} className="relative">
            <img
              src={miniaturas[indice]}
              alt={arquivo.name}
              className="size-16 rounded-lg object-cover ring-1 ring-slate-200"
            />
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(arquivos.filter((_, outro) => outro !== indice))}
              aria-label={`Remover ${arquivo.name}`}
              className="absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full bg-slate-800 text-xs text-white hover:bg-slate-900"
            >
              ×
            </button>
          </div>
        ))}

        {arquivos.length < MAX_ANEXOS ? (
          <label
            className={`inline-flex cursor-pointer items-center rounded-lg px-3 py-2 text-xs font-medium text-slate-600 ring-1 ring-slate-200 ring-inset hover:bg-slate-50 ${disabled ? 'pointer-events-none opacity-50' : ''}`}
          >
            Anexar imagem
            <input
              type="file"
              accept={TIPOS.join(',')}
              multiple
              className="sr-only"
              disabled={disabled}
              onChange={(evento) => {
                escolher(evento.target.files);
                evento.target.value = '';
              }}
            />
          </label>
        ) : null}
        <span className="text-xs text-slate-400">
          JPG, PNG ou WebP, até 5 MB, no máximo {MAX_ANEXOS}.
        </span>
      </div>
      {aviso ? <p className="text-xs text-amber-700">{aviso}</p> : null}
    </div>
  );
}

/**
 * Envia os anexos de uma mensagem ja gravada. Devolve os que falharam: a
 * mensagem ja existe, entao a falha de uma imagem nao pode desfazer o resto.
 */
export async function enviarAnexos(
  ticketId: number,
  messageId: number,
  arquivos: File[],
): Promise<string[]> {
  const falhas: string[] = [];
  for (const arquivo of arquivos) {
    try {
      await anexarAoChamado(ticketId, messageId, arquivo);
    } catch (erro) {
      falhas.push(`${arquivo.name}: ${erro instanceof Error ? erro.message : 'falha no envio'}`);
    }
  }
  return falhas;
}
